/**
 * Geometry utilities shared by the browser and the Node server.
 * Coordinates are [x, y] arrays (lon/lat degrees or planar metres).
 */

const RAD = Math.PI / 180;

/* ---------------- rings & bounding boxes ---------------- */

export function openRing(ring) {
  if (ring.length > 1) {
    const a = ring[0], b = ring[ring.length - 1];
    if (a[0] === b[0] && a[1] === b[1]) return ring.slice(0, -1);
  }
  return ring;
}

export function closeRing(ring) {
  if (!ring.length) return ring;
  const a = ring[0], b = ring[ring.length - 1];
  return a[0] === b[0] && a[1] === b[1] ? ring : [...ring, [a[0], a[1]]];
}

function extendBBox(bb, coords) {
  if (typeof coords[0] === 'number') {
    if (coords[0] < bb[0]) bb[0] = coords[0];
    if (coords[1] < bb[1]) bb[1] = coords[1];
    if (coords[0] > bb[2]) bb[2] = coords[0];
    if (coords[1] > bb[3]) bb[3] = coords[1];
    return bb;
  }
  for (const c of coords) extendBBox(bb, c);
  return bb;
}

export function coordsBBox(coords) {
  return extendBBox([Infinity, Infinity, -Infinity, -Infinity], coords);
}

export function geometryBBox(geom) {
  if (!geom) return null;
  if (geom.type === 'GeometryCollection') {
    const bb = [Infinity, Infinity, -Infinity, -Infinity];
    for (const g of geom.geometries) { const b = geometryBBox(g); if (b) { extendBBox(bb, [b[0], b[1]]); extendBBox(bb, [b[2], b[3]]); } }
    return bb;
  }
  return coordsBBox(geom.coordinates);
}

export const bboxIntersects = (a, b) => a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
export const bboxContains = (bb, x, y) => x >= bb[0] && x <= bb[2] && y >= bb[1] && y <= bb[3];
export const bboxUnion = (a, b) => (a ? [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[2], b[2]), Math.max(a[3], b[3])] : b.slice());

/**
 * Polygonal parts of a geometry as an array of polygons (array of rings).
 * Closed LineStrings are treated as polygons only when `closedLines` is true.
 */
export function toPolygons(geom, closedLines = false) {
  if (!geom) return [];
  switch (geom.type) {
    case 'Polygon': return [geom.coordinates];
    case 'MultiPolygon': return geom.coordinates;
    case 'LineString': return closedLines && isClosed(geom.coordinates) ? [[geom.coordinates]] : [];
    case 'MultiLineString': return closedLines ? geom.coordinates.filter(isClosed).map((r) => [r]) : [];
    case 'GeometryCollection': return geom.geometries.flatMap((g) => toPolygons(g, closedLines));
    default: return [];
  }
}

function isClosed(r) {
  return r.length >= 4 && r[0][0] === r[r.length - 1][0] && r[0][1] === r[r.length - 1][1];
}

/* ---------------- point in polygon ---------------- */

/** Crossing-number test (works for open or closed rings). */
export function pointInRing(x, y, ring) {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function pointInPolygon(x, y, polygon) {
  if (!polygon.length || !pointInRing(x, y, polygon[0])) return false;
  for (let k = 1; k < polygon.length; k++) if (pointInRing(x, y, polygon[k])) return false;
  return true;
}

export function pointInGeometry(x, y, geom) {
  const bb = geom.bbox || geometryBBox(geom);
  if (!bboxContains(bb, x, y)) return false;
  return toPolygons(geom).some((p) => pointInPolygon(x, y, p));
}

/** Minimal distance (metres, local equirectangular) from a lon/lat point to polygon edges. */
export function distanceToGeometryEdges(lon, lat, geom) {
  const kx = 111319.49 * Math.cos(lat * RAD), ky = 110574.0;
  let best = Infinity;
  for (const poly of toPolygons(geom, true)) {
    for (const ring of poly) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const ax = (ring[j][0] - lon) * kx, ay = (ring[j][1] - lat) * ky;
        const bx = (ring[i][0] - lon) * kx, by = (ring[i][1] - lat) * ky;
        const d = segDist2(0, 0, ax, ay, bx, by);
        if (d < best) best = d;
      }
    }
  }
  return Math.sqrt(best);
}

function segDist2(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const l2 = dx * dx + dy * dy;
  let t = l2 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const qx = ax + t * dx - px, qy = ay + t * dy - py;
  return qx * qx + qy * qy;
}

/* ---------------- planar measures ---------------- */

export function ringSignedArea(ring) {
  let s = 0;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) s += (ring[j][0] - ring[i][0]) * (ring[j][1] + ring[i][1]);
  return s / 2; // >0 for counter-clockwise (y up)
}

export const ringArea = (ring) => Math.abs(ringSignedArea(ring));

export function polygonArea(polygon) {
  let a = ringArea(polygon[0] || []);
  for (let k = 1; k < polygon.length; k++) a -= ringArea(polygon[k]);
  return Math.max(0, a);
}

export function ringPerimeter(ring, closed = true) {
  let s = 0;
  const r = openRing(ring);
  for (let i = 1; i < r.length; i++) s += Math.hypot(r[i][0] - r[i - 1][0], r[i][1] - r[i - 1][1]);
  if (closed && r.length > 2) s += Math.hypot(r[0][0] - r[r.length - 1][0], r[0][1] - r[r.length - 1][1]);
  return s;
}

export function ringCentroid(ring) {
  const r = openRing(ring);
  let a = 0, cx = 0, cy = 0;
  const x0 = r[0][0], y0 = r[0][1];
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const xi = r[i][0] - x0, yi = r[i][1] - y0, xj = r[j][0] - x0, yj = r[j][1] - y0;
    const f = xj * yi - xi * yj;
    a += f; cx += (xj + xi) * f; cy += (yj + yi) * f;
  }
  if (Math.abs(a) < 1e-18) {
    const bb = coordsBBox(r);
    return [(bb[0] + bb[2]) / 2, (bb[1] + bb[3]) / 2];
  }
  return [x0 + cx / (3 * a), y0 + cy / (3 * a)];
}

/** A point guaranteed to lie inside the polygon (centroid when possible, scan-line otherwise). */
export function interiorPoint(polygon) {
  const c = ringCentroid(polygon[0]);
  if (pointInPolygon(c[0], c[1], polygon)) return c;
  const bb = coordsBBox(polygon[0]);
  let best = null, bestW = -1;
  for (let k = 1; k <= 9; k++) {
    const y = bb[1] + ((bb[3] - bb[1]) * k) / 10;
    const xs = [];
    for (const ring of polygon) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i], [xj, yj] = ring[j];
        if ((yi > y) !== (yj > y)) xs.push(xi + ((y - yi) * (xj - xi)) / (yj - yi));
      }
    }
    xs.sort((a, b) => a - b);
    for (let i = 0; i + 1 < xs.length; i += 2) {
      const w = xs[i + 1] - xs[i];
      if (w > bestW) { bestW = w; best = [(xs[i] + xs[i + 1]) / 2, y]; }
    }
  }
  return best || c;
}

/* ---------------- geodesic distance (WGS84, Vincenty) ---------------- */

export function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371008.8;
  const dLat = (lat2 - lat1) * RAD, dLon = (lon2 - lon1) * RAD;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * RAD) * Math.cos(lat2 * RAD) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function vincenty(lat1, lon1, lat2, lon2) {
  const a = 6378137, f = 1 / 298.257223563, b = a * (1 - f);
  const L = (lon2 - lon1) * RAD;
  const U1 = Math.atan((1 - f) * Math.tan(lat1 * RAD));
  const U2 = Math.atan((1 - f) * Math.tan(lat2 * RAD));
  const sinU1 = Math.sin(U1), cosU1 = Math.cos(U1), sinU2 = Math.sin(U2), cosU2 = Math.cos(U2);
  let lambda = L, iter = 0, sinSigma, cosSigma, sigma, cosSqAlpha, cos2SigmaM;
  do {
    const sinL = Math.sin(lambda), cosL = Math.cos(lambda);
    sinSigma = Math.sqrt((cosU2 * sinL) ** 2 + (cosU1 * sinU2 - sinU1 * cosU2 * cosL) ** 2);
    if (sinSigma === 0) return 0;
    cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosL;
    sigma = Math.atan2(sinSigma, cosSigma);
    const sinAlpha = (cosU1 * cosU2 * sinL) / sinSigma;
    cosSqAlpha = 1 - sinAlpha * sinAlpha;
    cos2SigmaM = cosSqAlpha ? cosSigma - (2 * sinU1 * sinU2) / cosSqAlpha : 0;
    const C = (f / 16) * cosSqAlpha * (4 + f * (4 - 3 * cosSqAlpha));
    const prev = lambda;
    lambda = L + (1 - C) * f * sinAlpha * (sigma + C * sinSigma * (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM ** 2)));
    if (Math.abs(lambda - prev) < 1e-12) break;
  } while (++iter < 200);
  if (iter >= 200) return haversine(lat1, lon1, lat2, lon2);
  const uSq = (cosSqAlpha * (a * a - b * b)) / (b * b);
  const A = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
  const B = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
  const dSigma = B * sinSigma * (cos2SigmaM + (B / 4) * (cosSigma * (-1 + 2 * cos2SigmaM ** 2) - (B / 6) * cos2SigmaM * (-3 + 4 * sinSigma ** 2) * (-3 + 4 * cos2SigmaM ** 2)));
  return b * A * (sigma - dSigma);
}

/* ---------------- simplification ---------------- */

/** Douglas–Peucker on an open or closed ring/line; keeps endpoints. */
export function simplifyLine(pts, tol) {
  const n = pts.length;
  if (n <= 3 || tol <= 0) return pts.slice();
  const keep = new Uint8Array(n);
  keep[0] = keep[n - 1] = 1;
  const tol2 = tol * tol;
  const stack = [[0, n - 1]];
  while (stack.length) {
    const [s, e] = stack.pop();
    let maxD = -1, idx = -1;
    const ax = pts[s][0], ay = pts[s][1], bx = pts[e][0], by = pts[e][1];
    for (let i = s + 1; i < e; i++) {
      const d = segDist2(pts[i][0], pts[i][1], ax, ay, bx, by);
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (idx >= 0 && maxD > tol2) { keep[idx] = 1; stack.push([s, idx], [idx, e]); }
  }
  const out = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(pts[i]);
  return out;
}

function simplifyRingKeepShape(ring, tol) {
  if (ring.length <= 5) return ring;
  // split the closed ring at its farthest vertex from the start for a stable result
  const r = openRing(ring);
  let far = 0, fd = -1;
  for (let i = 1; i < r.length; i++) {
    const d = (r[i][0] - r[0][0]) ** 2 + (r[i][1] - r[0][1]) ** 2;
    if (d > fd) { fd = d; far = i; }
  }
  const a = simplifyLine(r.slice(0, far + 1), tol);
  const b = simplifyLine([...r.slice(far), r[0]], tol);
  const out = [...a, ...b.slice(1)];
  return out.length >= 4 ? out : closeRing(r);
}

const quant = (v, q) => Math.round(v * q) / q;

/** Simplify + quantize a (Multi)Polygon / (Multi)LineString geometry. tol in coordinate units. */
export function simplifyGeometry(geom, tol = 0, q = 1e7) {
  if (!geom) return geom;
  const qr = (ring) => ring.map((p) => [quant(p[0], q), quant(p[1], q)]);
  const sr = (ring) => qr(tol > 0 ? simplifyRingKeepShape(ring, tol) : ring);
  const sl = (line) => qr(tol > 0 ? simplifyLine(line, tol) : line);
  switch (geom.type) {
    case 'Polygon': return { type: 'Polygon', coordinates: geom.coordinates.map(sr) };
    case 'MultiPolygon': return { type: 'MultiPolygon', coordinates: geom.coordinates.map((p) => p.map(sr)) };
    case 'LineString': return { type: 'LineString', coordinates: sl(geom.coordinates) };
    case 'MultiLineString': return { type: 'MultiLineString', coordinates: geom.coordinates.map(sl) };
    case 'Point': return { type: 'Point', coordinates: qr([geom.coordinates])[0] };
    default: return geom;
  }
}

export function countVertices(geom) {
  let n = 0;
  const walk = (c) => { if (typeof c[0] === 'number') n++; else c.forEach(walk); };
  if (geom && geom.coordinates) walk(geom.coordinates);
  return n;
}

/* ---------------- triangulation & clipping ---------------- */

const cross3 = (a, b, c) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);

function pointInTri(p, a, b, c) {
  const d1 = cross3(a, b, p), d2 = cross3(b, c, p), d3 = cross3(c, a, p);
  return d1 >= 0 && d2 >= 0 && d3 >= 0;
}

/**
 * Ear-clipping triangulation of a simple polygon (no holes).
 * Returns counter-clockwise triangles [[a, b, c], ...].
 */
export function triangulate(ring) {
  let pts = [];
  for (const p of openRing(ring)) {
    const q = pts[pts.length - 1];
    if (!q || q[0] !== p[0] || q[1] !== p[1]) pts.push(p);
  }
  if (pts.length > 1 && pts[0][0] === pts[pts.length - 1][0] && pts[0][1] === pts[pts.length - 1][1]) pts.pop();
  if (pts.length < 3) return [];
  if (ringSignedArea(pts) < 0) pts = pts.slice().reverse();
  const bb = coordsBBox(pts);
  const eps = 1e-12 * Math.max(1, (bb[2] - bb[0]) * (bb[3] - bb[1]));
  const idx = pts.map((_, i) => i);
  const tris = [];
  let guard = 0;
  const limit = pts.length * pts.length + 10;
  while (idx.length > 3 && guard++ < limit) {
    let clipped = false;
    for (let i = 0; i < idx.length; i++) {
      const i0 = idx[(i + idx.length - 1) % idx.length], i1 = idx[i], i2 = idx[(i + 1) % idx.length];
      const a = pts[i0], b = pts[i1], c = pts[i2];
      const cr = cross3(a, b, c);
      if (Math.abs(cr) <= eps) { idx.splice(i, 1); clipped = true; break; } // collinear / spike
      if (cr < 0) continue; // reflex
      let blocked = false;
      for (let j = 0; j < idx.length && !blocked; j++) {
        const k = idx[j];
        if (k === i0 || k === i1 || k === i2) continue;
        const p = pts[k];
        if ((p[0] === a[0] && p[1] === a[1]) || (p[0] === b[0] && p[1] === b[1]) || (p[0] === c[0] && p[1] === c[1])) continue;
        if (pointInTri(p, a, b, c)) blocked = true;
      }
      if (blocked) continue;
      tris.push([a, b, c]);
      idx.splice(i, 1);
      clipped = true;
      break;
    }
    if (!clipped) break; // not simple — stop gracefully
  }
  if (idx.length === 3) {
    const t = [pts[idx[0]], pts[idx[1]], pts[idx[2]]];
    if (Math.abs(cross3(...t)) > eps) tris.push(t);
  }
  return tris;
}

/** Sutherland–Hodgman: clip any ring by a convex counter-clockwise polygon. */
export function clipRingConvex(subject, clip) {
  let out = openRing(subject);
  const m = clip.length;
  for (let i = 0; i < m && out.length; i++) {
    const c1 = clip[i], c2 = clip[(i + 1) % m];
    const inp = out;
    out = [];
    let s = inp[inp.length - 1];
    let sIn = cross3(c1, c2, s) >= 0;
    for (const e of inp) {
      const eIn = cross3(c1, c2, e) >= 0;
      if (eIn) {
        if (!sIn) out.push(intersect(s, e, c1, c2));
        out.push(e);
      } else if (sIn) out.push(intersect(s, e, c1, c2));
      s = e; sIn = eIn;
    }
  }
  return out;
}

function intersect(s, e, a, b) {
  const dx = e[0] - s[0], dy = e[1] - s[1];
  const ex = b[0] - a[0], ey = b[1] - a[1];
  const den = dx * ey - dy * ex;
  if (den === 0) return [e[0], e[1]];
  const t = ((a[0] - s[0]) * ey - (a[1] - s[1]) * ex) / den;
  return [s[0] + t * dx, s[1] + t * dy];
}

/** Clip ring by an axis-aligned box (convex) — cheap pre-filter for huge rings. */
export function clipRingBBox(ring, bb) {
  return clipRingConvex(ring, [[bb[0], bb[1]], [bb[2], bb[1]], [bb[2], bb[3]], [bb[0], bb[3]]]);
}

/**
 * Exact area of (union of triangles) ∩ polygon(s). Triangles must not overlap
 * (output of triangulate). Polygons: array of [outer, ...holes] in the same planar units.
 * Returns { area, pieces } where pieces are Polygon coordinate arrays for display.
 */
export function overlayArea(triangles, polygons, wantPieces = false) {
  let area = 0;
  const pieces = [];
  for (const tri of triangles) {
    const tb = coordsBBox(tri);
    for (const poly of polygons) {
      const pb = poly.bbox || (poly.bbox = coordsBBox(poly[0]));
      if (!bboxIntersects(tb, pb)) continue;
      const outer = clipRingConvex(poly[0], tri);
      if (outer.length < 3) continue;
      let a = ringArea(outer);
      if (a <= 0) continue;
      const holes = [];
      for (let k = 1; k < poly.length; k++) {
        const hb = coordsBBox(poly[k]);
        if (!bboxIntersects(tb, hb)) continue;
        const h = clipRingConvex(poly[k], tri);
        if (h.length >= 3) {
          const ha = ringArea(h);
          if (ha > 0) { a -= ha; holes.push(h); }
        }
      }
      if (a <= 0) continue;
      area += a;
      if (wantPieces) pieces.push([closeRing(outer), ...holes.map(closeRing)]);
    }
  }
  return { area, pieces };
}

/* ---------------- validation ---------------- */

function segInter(p1, p2, p3, p4) {
  const d1 = cross3(p3, p4, p1), d2 = cross3(p3, p4, p2), d3 = cross3(p1, p2, p3), d4 = cross3(p1, p2, p4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

/** Validate a simple polygon ring (planar). */
export function validateRing(ring) {
  const r = openRing(ring);
  const issues = [];
  if (r.length < 3) issues.push('few-points');
  const n = r.length;
  let crossing = null;
  for (let i = 0; i < n && !crossing; i++) {
    const a1 = r[i], a2 = r[(i + 1) % n];
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      if (segInter(a1, a2, r[j], r[(j + 1) % n])) { crossing = [i, j]; break; }
    }
  }
  if (crossing) issues.push('self-intersection');
  if (n >= 3 && ringArea(r) < 1e-9) issues.push('zero-area');
  return { ok: issues.length === 0, issues, crossing };
}

/* ---------------- web-mercator tiles ---------------- */

export function lngLatToTile(lng, lat, z) {
  const n = 2 ** z;
  const x = Math.floor(((lng + 180) / 360) * n);
  const s = Math.sin(lat * RAD);
  const y = Math.floor((0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n);
  return { x: Math.min(n - 1, Math.max(0, x)), y: Math.min(n - 1, Math.max(0, y)) };
}

export function tileBBox(x, y, z) {
  const n = 2 ** z;
  const lon = (t) => (t / n) * 360 - 180;
  const lat = (t) => Math.atan(Math.sinh(Math.PI * (1 - (2 * t) / n))) / RAD;
  return [lon(x), lat(y + 1), lon(x + 1), lat(y)];
}

export function tilesForBBox(bb, z) {
  const a = lngLatToTile(bb[0], bb[3], z), b = lngLatToTile(bb[2], bb[1], z);
  const out = [];
  for (let x = a.x; x <= b.x; x++) for (let y = a.y; y <= b.y; y++) out.push({ x, y, z });
  return out;
}
